/**
 * Smart tiered image compression (browser-side only).
 *
 * Default output is WebP for maximum size reduction (web display).
 * Pass { format: 'jpeg' } for images that will be sent as WhatsApp media
 * (coupon / broadcast) — WhatsApp treats WebP as stickers and drops them.
 *
 * - <= 300 KB: skip compression
 * - 300 KB ~ 1 MB: maxWidth 1200, quality 0.60 (webp) / 0.80 (jpeg)
 * - 1 MB ~ 3 MB: maxWidth 1000, quality 0.50 / 0.75
 * - > 3 MB: maxWidth 800, quality 0.40 / 0.70
 * - SVG / GIF: skip compression
 * - If compressed result is not smaller, return original file
 *
 * Source: GoodStore2 porting guide
 */

export interface CompressResult {
  isCompressed: boolean;
  file: File;
  originalSize: number;
  compressedSize: number;
  ratio: string;
}

export interface CompressOptions {
  /**
   * Output format. Default 'webp'（檔案最細，網頁顯示用）。
   * Use 'jpeg' for images that will be sent as WhatsApp media (優惠券 / 廣播)：
   * WhatsApp treats WebP as a sticker format and silently drops webp images,
   * so coupon images never arrive. JPEG is safe everywhere.
   */
  format?: 'webp' | 'jpeg';
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export async function smartCompressImage(file: File, options: CompressOptions = {}): Promise<CompressResult> {
  const format = options.format ?? 'webp';
  if (
    file.type === 'image/svg+xml' ||
    file.type === 'image/gif' ||
    file.size <= 300 * 1024
  ) {
    return {
      isCompressed: false,
      file,
      originalSize: file.size,
      compressedSize: file.size,
      ratio: '0%',
    };
  }

  const fileSizeKB = file.size / 1024;
  let maxWidth = 1200;
  // JPEG is less size-efficient than WebP, so jpeg tiers use higher quality
  // to keep the same visual result.
  let quality = format === 'jpeg' ? 0.8 : 0.6;

  if (fileSizeKB > 3072) {
    maxWidth = 800;
    quality = format === 'jpeg' ? 0.7 : 0.4;
  } else if (fileSizeKB > 1024) {
    maxWidth = 1000;
    quality = format === 'jpeg' ? 0.75 : 0.5;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve({
          isCompressed: false,
          file,
          originalSize: file.size,
          compressedSize: file.size,
          ratio: '0%',
        });
        return;
      }

      // JPEG has no alpha channel: fill white first so transparent PNGs
      // don't turn black.
      if (format === 'jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
      }
      ctx.drawImage(img, 0, 0, width, height);

      const outputType = format === 'jpeg' ? 'image/jpeg' : 'image/webp';
      const ext = format === 'jpeg' ? 'jpg' : 'webp';

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            resolve({
              isCompressed: false,
              file,
              originalSize: file.size,
              compressedSize: file.size,
              ratio: '0%',
            });
            return;
          }

          const originalName = file.name.replace(/\.[^.]+$/, '');
          const compressedFile = new File([blob], `${originalName}.${ext}`, {
            type: outputType,
            lastModified: Date.now(),
          });

          const ratio = (((file.size - blob.size) / file.size) * 100).toFixed(0);

          resolve({
            isCompressed: true,
            file: compressedFile,
            originalSize: file.size,
            compressedSize: blob.size,
            ratio: `${ratio}%`,
          });
        },
        outputType,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        isCompressed: false,
        file,
        originalSize: file.size,
        compressedSize: file.size,
        ratio: '0%',
      });
    };

    img.src = objectUrl;
  });
}
